/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import { Dialog } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import styles from './ConfirmCheckoutModal.module.css';

const DotGroup = () => (
  <span className={styles.dotGroup}>
    <span className={styles.dot} />
    <span className={styles.dot} />
    <span className={styles.dot} />
    <span className={styles.dot} />
  </span>
);

const intervalLabel = (days) => {
  if (days === '36500') return 'Lifetime';
  if (days === '365') return 'Annual';
  if (days === '30') return 'Monthly';
  if (days === '7') return 'Weekly';
  return 'Daily';
};

const billingCadenceText = (days) => {
  if (days === '36500') return 'Billed for a Lifetime';
  if (days === '365') return 'Billed Annually';
  if (days === '30') return 'Billed Monthly';
  if (days === '7') return 'Billed Weekly';
  return 'Billed Daily';
};

const formatMoney = (value) => {
  if (value === undefined || value === null || value === '') return '0.00';
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

const formatBrand = (brand) => {
  if (!brand) return '';
  const b = brand.toLowerCase();
  if (b === 'amex' || b === 'american express' || b === 'american_express') {
    return 'Amex';
  }
  return b.charAt(0).toUpperCase() + b.slice(1);
};

const formatExpiry = (month, year) => {
  if (!month || !year) return '';
  const mm = String(month).padStart(2, '0');
  const yy = String(year).slice(-2);
  return `${mm}/${yy}`;
};

const ConfirmCheckoutModal = ({
  isVisible,
  onCancel,
  onConfirm,
  cardLast4,
  cardBrand,
  cardExpMonth,
  cardExpYear,
  promoCode,
  loading,
  planPrice,
  selectedPlan,
  discountedPrice,
  calculatedDiscount,
  promoName,
  isTrail,
  trialVal,
}) => {
  const intervalDays = selectedPlan?.interval_in_days;
  const regularPrice = planPrice?.amount ?? planPrice;
  const hasDiscount = discountedPrice !== '' && discountedPrice !== undefined && discountedPrice !== null;
  const isTrialActive = isTrail === '1' || trialVal === '1';
  const totalPostTrial = hasDiscount ? discountedPrice : regularPrice;

  return (
    <Dialog
      open={isVisible}
      keepMounted
      aria-labelledby="confirm-checkout-title"
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: styles.dialogPaper }}
    >
      <div className={styles.container}>
        {!loading && (
          <CloseIcon
            className={styles.closeButton}
            onClick={onCancel}
            aria-label="Close"
          />
        )}

        <div className={styles.iconWrap}>
          <div className={styles.iconBadge}>
            <LockOutlinedIcon />
          </div>
        </div>

        <h2 id="confirm-checkout-title" className={styles.title}>
          Confirm Your Purchase
        </h2>

        <div className={styles.subtitleCardGroup}>
          <p className={styles.subtitle}>
            Review the payment details below before continuing
          </p>

          <div className={styles.cardPreview}>
          <div className={styles.cardTopRow}>
            <span className={styles.cardLabel}>Default Card</span>
            <span className={styles.chip} aria-hidden />
          </div>

          <div className={styles.cardNumber}>
            <DotGroup />
            <DotGroup />
            <DotGroup />
            <span className={styles.last4}>{cardLast4 || '••••'}</span>
          </div>

          {formatExpiry(cardExpMonth, cardExpYear) && (
            <div className={styles.cardExpiry}>
              <span className={styles.cardExpiryLabel}>Valid Thru</span>
              <span className={styles.cardExpiryValue}>
                {formatExpiry(cardExpMonth, cardExpYear)}
              </span>
            </div>
          )}

          <div className={styles.cardFooter}>
            <div className={styles.cardBrand}>
              {formatBrand(cardBrand) || 'Card'}
            </div>
          </div>
          </div>
        </div>

        {promoCode ? (
          <div className={styles.promoChip}>
            <div className={styles.promoIcon}>
              <LocalOfferOutlinedIcon />
            </div>
            <div className={styles.promoText}>
              <span className={styles.promoLabel}>
                {promoName ? `${promoName} Applied` : 'Promo Applied'}
              </span>
              <span className={styles.promoValue}>{promoCode}</span>
            </div>
          </div>
        ) : null}

        <div className={styles.summary}>
          <div className={styles.summaryHeader}>Order Summary</div>

          <div className={styles.summaryRow}>
            <div className={styles.summaryPlan}>
              <span className={styles.summaryPlanName}>
                Projector
                {' '}
                {intervalLabel(intervalDays)}
              </span>
              <span className={styles.summaryPlanCadence}>
                {billingCadenceText(intervalDays)}
              </span>
            </div>
            <span className={styles.summaryAmount}>
              {`$${formatMoney(regularPrice)}`}
            </span>
          </div>

          {hasDiscount && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryDiscountLabel}>
                {promoName || 'Promo discount'}
              </span>
              <span className={styles.summaryDiscountValue}>
                {`- $${formatMoney(calculatedDiscount)}`}
              </span>
            </div>
          )}

          <div className={styles.summaryDivider} />

          <div className={styles.summaryTotalRow}>
            <span className={styles.summaryTotalLabel}>Total Due Today</span>
            <span className={styles.summaryTotalValue}>
              {isTrialActive
                ? '$0.00'
                : `$${formatMoney(hasDiscount ? discountedPrice : regularPrice)}`}
            </span>
          </div>

          {isTrialActive && (
            <div className={styles.summaryTrialNote}>
              <span>
                Charged in
                {' '}
                {selectedPlan?.total_trial_days}
                {' '}
                {selectedPlan?.total_trial_days === '1' ? 'day' : 'days'}
              </span>
              <strong>{`$${formatMoney(totalPostTrial)}`}</strong>
            </div>
          )}
        </div>

        <p className={styles.message}>
          Your plan will be purchased using your default card attached.
          Do you want to proceed?
        </p>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <svg
                className="loading-circle_mini"
                width="18"
                height="18"
                viewBox="0 0 50 50"
              >
                <circle
                  className="circle"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                />
              </svg>
            ) : (
              <>
                <LockOutlinedIcon style={{ fontSize: 16 }} />
                Confirm &amp; Pay
              </>
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmCheckoutModal;
